<script lang="ts">
  import type { Snippet } from 'svelte';
  import {
    anchorFrame,
    frameAt,
    type FrameId,
    type FramePos,
  } from '../../lib/frames';
  import { settings } from '../../lib/settings.svelte';

  let {
    id,
    label,
    initial,
    free = false,
    onmenu,
    children,
  }: {
    id: FrameId;
    label: string;
    initial: FramePos;
    /** Drags by its own body (but not its buttons) even while frames are locked. */
    free?: boolean;
    /** Right click while unlocked. */
    onmenu: (event: MouseEvent) => void;
    children: Snippet;
  } = $props();

  let innerWidth = $state(0);
  let innerHeight = $state(0);
  let w = $state(0);
  let h = $state(0);
  // Inside the zoomed UI, offset sizes are unscaled and the screen is the window over the scale.
  const screen = $derived({
    w: innerWidth / settings.uiScale,
    h: innerHeight / settings.uiScale,
  });
  const at = $derived(
    frameAt(settings.frames[id] ?? initial, { w, h }, screen),
  );
  let grab: { x: number; y: number; left: number; top: number } | null =
    $state(null);

  function down(e: PointerEvent) {
    if (e.button !== 0 || (e.target as Element).closest('button')) return;
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    grab = { x: e.clientX, y: e.clientY, ...at };
  }

  function move(e: PointerEvent) {
    if (!grab) return;
    const scale = settings.uiScale;
    settings.frames[id] = anchorFrame(
      grab.left + (e.clientX - grab.x) / scale,
      grab.top + (e.clientY - grab.y) / scale,
      { w, h },
      screen,
    );
  }
</script>

<svelte:window bind:innerWidth bind:innerHeight />

<div
  class="movable"
  class:unlocked={!settings.lockFrames}
  class:free
  style:left="{at.left}px"
  style:top="{at.top}px"
  bind:offsetWidth={w}
  bind:offsetHeight={h}
  role="presentation"
  onpointerdown={free && settings.lockFrames ? down : undefined}
  onpointermove={free && settings.lockFrames ? move : undefined}
  onpointerup={() => (grab = null)}
>
  {@render children()}
  {#if !settings.lockFrames}
    <div
      class="handle"
      class:grabbed={grab}
      role="presentation"
      onpointerdown={down}
      onpointermove={move}
      onpointerup={() => (grab = null)}
      oncontextmenu={(e) => {
        e.preventDefault();
        onmenu(e);
      }}
    >
      <span>{label}</span>
    </div>
  {/if}
</div>

<style>
  .movable {
    position: absolute;
  }
  .free {
    cursor: grab;
  }
  .unlocked {
    min-width: 200px;
    min-height: 40px;
  }
  .handle {
    position: absolute;
    inset: -3px;
    display: grid;
    place-items: center;
    color: var(--gold);
    font-size: 13px;
    font-weight: bold;
    text-shadow: 0 1px 2px black;
    background: rgba(230, 192, 106, 0.12);
    border: 2px dashed var(--gold);
    border-radius: 6px;
    cursor: grab;
    pointer-events: auto;
    user-select: none;
    touch-action: none;
  }
  .handle span {
    padding: 1px 6px;
    background: rgba(0, 0, 0, 0.7);
    border-radius: 3px;
  }
  .handle.grabbed {
    cursor: grabbing;
    background: rgba(230, 192, 106, 0.25);
  }
</style>
